import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import CobuConstants from '~/helpers/cobuConstants';
import { ResidentInputFieldConfig } from '~/types/residentManagement';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import { SelectedOptionType } from '~/types/form';
import { SelectType } from '~/enums/SelectType';
import Spacer from '../../components/Spacer/Spacer';
import { toast } from 'react-toastify';
import { externalURLFormConfig } from '~/helpers/formHelpers';
import { UniversalButton } from '~/types/universalButton';

type CreateUniversalButtonFormProps = {
  createUniversalButton: (r: any) => void;
  updateUniversalButton: (r: any) => void;
  cancel: () => void;
  formConfig: ResidentInputFieldConfig[];
  defaultData: UniversalButton | null;
};

const universalButtonTypes: SelectedOptionType[] = [
  {
    value: 'Referral',
    label: 'Referral Details'
  },
  {
    value: 'Events',
    label: 'Events Page'
  },
  {
    value: 'Groups',
    label: 'Groups Page'
  },
  {
    value: 'Perks',
    label: 'Perks Page'
  },
  {
    value: 'Marketplace',
    label: 'Marketplace'
  },
  {
    value: 'External',
    label: 'External'
  },
  {
    value: 'CreateGroupEvent',
    label: 'Create Group Event'
  }
];

const CreateUniversalButtonForm = ({
  cancel,
  createUniversalButton,
  updateUniversalButton,
  formConfig,
  defaultData
}: CreateUniversalButtonFormProps) => {
  const [multiValue, setMultiValue] = useState<SelectedOptionType[]>(() =>
    defaultData
      ? [{ label: defaultData.buildingName, value: defaultData.building }]
      : []
  );
  const [multiValueType, setMultiValueType] = useState<SelectedOptionType[]>(
    () => {
      if (!defaultData) {
        return [];
      } else {
        const foundLinkType = universalButtonTypes.find(
          (typeKeyValue) => typeKeyValue.value === defaultData.linkType
        );

        return foundLinkType ? [foundLinkType] : [];
      }
    }
  );

  const { register, handleSubmit, errors } = useForm<any>({
    defaultValues: defaultData ?? {}
  });

  const onSubmit: SubmitHandler<any> = (data) => {
    if (multiValue.length === 0) {
      toast(CobuConstants.createSurvey.buildingNotSelectedWarning);
      return;
    }
    let building = multiValue[0].value;
    if (multiValueType.length === 0) {
      toast(CobuConstants.createSurvey.buildingNotSelectedWarning);
      return;
    }
    let linkType = multiValueType[0].value;

    if (defaultData) {
      updateUniversalButton({
        ...data,
        uuid: defaultData.uuid,
        building,
        linkType,
        linkURL: linkType === 'External' ? data.linkURL : ''
      });
      return;
    }
    createUniversalButton({
      ...data,
      building,
      linkType
    });
  };

  const externalFormField = () => {
    if (multiValueType.length) {
      let buttonType = multiValueType[0].value;
      if (buttonType === 'External') {
        return generateField(externalURLFormConfig);
      }
    }
    return null;
  };

  const generateField = (fieldConfig: ResidentInputFieldConfig) => {
    let error = errors[fieldConfig.id];

    return (
      <div key={fieldConfig.id}>
        <div className='item'>
          <label htmlFor='title'>{fieldConfig.label}</label>
          <input
            type='text'
            id={fieldConfig.id}
            name={fieldConfig.id}
            readOnly={fieldConfig.readOnly}
            ref={register({
              required: true,
              pattern: fieldConfig.pattern,
              maxLength: fieldConfig.maxLength,
              minLength: fieldConfig.minLength
            })}
            className={`${fieldConfig.readOnly && 'nonEditable'} ${
              error && 'border-red'
            }`}
          />
          {error && error.type === 'required' && (
            <span className='errorTitle' role='alert'>
              {CobuConstants.createResident.requiredError}
            </span>
          )}
          {error && fieldConfig.pattern && error.type === 'pattern' && (
            <span className='errorTitle' role='alert'>
              {fieldConfig.patternErrorMsg}
            </span>
          )}
          {error && fieldConfig.maxLengthError && error.type === 'maxLength' && (
            <span className='errorTitle' role='alert'>
              {fieldConfig.maxLengthError}
            </span>
          )}
          {error && fieldConfig.maxLengthError && error.type === 'minLength' && (
            <span className='errorTitle' role='alert'>
              {fieldConfig.minLengthError}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='gridContainer'>
          <div>
            <label htmlFor='title'>
              {CobuConstants.createUniversalButton.building}
            </label>
            <Spacer height={8} />
            <MultiSelect
              singleSelect
              disabled={!!defaultData}
              asyncBuildings
              selectType={SelectType.Form}
              multiValue={multiValue ?? [{ label: '', value: '' }]}
              setMultiValue={setMultiValue}
              placeholder={CobuConstants.createSurvey.buildingPlaceholder}
              cobuPlusBuildings={[]}
            />
          </div>
          <div>
            <label htmlFor='title'>
              {CobuConstants.createUniversalButton.linkLocation}
            </label>
            <Spacer height={8} />
            <MultiSelect
              singleSelect
              options={universalButtonTypes}
              selectType={SelectType.Form}
              multiValue={multiValueType ?? [{ label: '', value: '' }]}
              setMultiValue={setMultiValueType}
              placeholder={CobuConstants.createSurvey.selectLinkLocation}
              cobuPlusBuildings={[]}
            />
          </div>
          {formConfig.map((field) => generateField(field))}
          {externalFormField()}
        </div>
        <div className='formButtons'>
          {!defaultData && (
            // Edit flow doesn't require cancel button. It's only for create flow.
            <button className='btn-standard btn-secondary' onClick={cancel}>
              {CobuConstants.createResident.cancel}
            </button>
          )}
          <button
            className='btn-standard btn-primary margin-left-16 '
            type='submit'
          >
            {defaultData ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateUniversalButtonForm;
